import { useState } from 'react';

import { Stack } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { Card, DataGrid, DataGridToolbar, useColDefs } from '~components';
import { pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType } from '~enums';
import OrganisationsService from '~features/organisations/services/organisationsService';
import Organisation, { organisationGridCols } from '~interfaces/organisation';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../../themes/dimens';

const persistenceKey = 'organisations';

const service = getService(OrganisationsService);

const OrganisationsDataGrid = () => {
	const apiRef = useGridApiRef();
	const [searchParams] = useSearchParams();
	const [totalCount, setTotalCount] = useState<number>(0);

	const { data, error, isLoading, isValidating } = useSWR(
		service.getOrganisationsCacheKey(searchParams),
		(_args: (string | URLSearchParams)[], options: { signal?: AbortSignal }) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;
			return service.getOrganisations({
				page: page,
				pageSize: pageSize,
				searchQuery: searchQuery,
				signal: options.signal,
			});
		},
		{
			onSuccess: (data) => setTotalCount(data?.total || 0),
			keepPreviousData: true,
			use: [cancellationMiddleware],
		}
	);

	const { id, name, gridColDefFor } = useColDefs<Organisation>();
	const columns: GridColDef<Organisation>[] = [
		id(pagePathSegments.Organisations),
		name(true, pagePathSegments.Organisations),
		gridColDefFor(organisationGridCols.devicesCount),
		gridColDefFor(organisationGridCols.keysCount),
		gridColDefFor(organisationGridCols.usersCount),
		gridColDefFor(organisationGridCols.creationDate),
	];

	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
	];

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				<DataGridToolbar filters={actionBarFilters} />
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick={true}
					error={error != null}
					loading={isLoading || isValidating}
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					paginationModel={{
						page: Number(searchParams.get(queryParameters.Page)),
						pageSize: Number(searchParams.get(queryParameters.PageSize)),
					}}
				/>
			</Stack>
		</Card>
	);
};

export default OrganisationsDataGrid;
